import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useApp } from './app-context';
import { ChatStore, type ChatState } from './chat-store';

const Ctx = createContext<ChatStore | null>(null);

/** One chat store per signed-in account; torn down on sign-out or account switch. */
export function ChatProvider({ children }: { children: ReactNode }) {
  const { me, config, setMe } = useApp();
  const enabled = Boolean(me && config?.flags.messaging);
  const userId = enabled ? me!.id : null;
  const [store, setStore] = useState<ChatStore | null>(null);

  useEffect(() => {
    if (!userId) return;
    const s = new ChatStore(userId, () => setMe(null));
    // Publishing the store from the effect is the subscription to this external system.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStore(s);
    void s.start();
    return () => {
      s.stop();
      setStore(null);
    };
  }, [userId, setMe]);

  return <Ctx.Provider value={store}>{children}</Ctx.Provider>;
}

export function useChatStore(): ChatStore | null {
  return useContext(Ctx);
}

const EMPTY: ChatState = {
  conversations: [],
  loaded: false,
  messages: {},
  hasMore: {},
  pending: {},
  typing: {},
  connection: 'connecting',
};
const noopSubscribe = () => () => undefined;

export function useChat(): ChatState {
  const store = useChatStore();
  return useSyncExternalStore(store?.subscribe ?? noopSubscribe, store ? store.getSnapshot : () => EMPTY);
}
